import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import React from 'react';
import {
    FormControlLabel, Checkbox, Typography, FormControl, MenuItem, InputLabel, Select,
    Paper, IconButton, Box, TextField, InputAdornment, ListItemText,
    Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { CircularProgress, Backdrop } from '@mui/material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import axiosInstance from '../../../../lib/axios';
import checkAndLock from '../../../../GlobalFunction/checkAndLock';
import UnLock from '../../../../GlobalFunction/UnLock';
import CrudButtons from '../../../../GlobalFunction/CrudButtons';
import { useReactToPrint } from 'react-to-print';
import { getRoomMasterdropdata, getShiftMasterdropdata, getYojnaMasterdropdata } from '../../../../Api/PropertyMst/Api';
import { selectGlobalProperty } from '../../../../redux/propertySlice';
import { useSelector } from 'react-redux';
import DailyTaskPrint from './DailyTaskPrint';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { keyframes } from '@emotion/react';


const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];



const rotate360 = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const MemoizedCheckbox = React.memo(({ checked, onChange, disabled, indeterminate, size = "small", sx = {} }) => (
    <Checkbox
        size={size}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        indeterminate={indeterminate}
        sx={{
            padding: '1px',
            margin: 0,
            '&.Mui-checked': { color: '#2196F3' },
            '&.MuiCheckbox-indeterminate': { color: '#2196F3' },
            ...sx
        }}
    />
));

const RemarkField = React.memo(({ task, isFormDisabled, onRemarkChange }) => {
    const [localRemark, setLocalRemark] = useState(task.remark || '');
    const timeoutRef = useRef(null);

    const handleChange = (e) => {
        const value = e.target.value;
        setLocalRemark(value);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            onRemarkChange(task, value);
        }, 300);
    };

    useEffect(() => {
        setLocalRemark(task.remark || '');
    }, [task.remark]);

    const isRemarkFromApi = task.remark && task.remark.trim() !== '';

    return (
        <TextField
            size="small"
            placeholder="Add remark..."
            value={localRemark}
            onChange={handleChange}
            disabled={isFormDisabled}
            variant="outlined"
            sx={{
                width: '220px',
                '& .MuiOutlinedInput-root': {
                    height: '28px',
                    fontSize: '0.75rem',
                    backgroundColor: isRemarkFromApi ? '#FFF9C4' : (isFormDisabled ? '#f1f1f1' : 'white'), // Light yellow background
                    '& input': {
                        color: isRemarkFromApi ? '#000' : 'inherit', // Dark text for better contrast on yellow
                        fontWeight: isRemarkFromApi ? 'bold' : 'normal',
                    },
                    '&:hover': {
                        backgroundColor: isRemarkFromApi ? '#FFF59D' : (isFormDisabled ? '#f5f5f5' : '#fafafa'),
                    }
                }
            }}
        />
    );
}, (prevProps, nextProps) => {
    return prevProps.task.remark === nextProps.task.remark &&
        prevProps.isFormDisabled === nextProps.isFormDisabled &&
        prevProps.task.actualDay === nextProps.task.actualDay &&
        prevProps.task.planDay === nextProps.task.planDay;
});

const TaskRow = React.memo(({ index, style, data }) => {
    const {
        filteredTasks,
        isFormDisabled,
        handleActualDayChange,
        handleRemarkChange,
        currentDay,
        taskIdMap
    } = data;

    const task = filteredTasks[index];
    if (!task) return null;

    const taskKey = `${task.yojanaName}_${task.areaName}_${task.taskName}`;

    return (
        <div style={style}>
            <Box
                sx={{
                    display: 'flex',
                    borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    height: '100%',
                    alignItems: 'center'
                }}
            >
                <Box sx={{ width: '60px', p: '4px 8px', fontSize: '0.875rem', flexShrink: 0 }}>
                    {index + 1}
                </Box>
                <Box sx={{ width: '250px', p: '4px 8px', fontSize: '0.875rem', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {task.yojanaName}
                </Box>
                <Box sx={{ width: '150px', p: '4px 8px', fontSize: '0.875rem', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {task.workStation || '-'}
                </Box>
                <Box sx={{ width: '150px', p: '4px 8px', fontSize: '0.875rem', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {task.areaName}
                </Box>
                <Box sx={{ width: '150px', p: '4px 8px', fontSize: '0.875rem', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={task.taskName} arrow>
                        {task.taskName}
                    </Tooltip>
                </Box>
                <Box sx={{ width: '250px', p: '4px 8px', fontSize: '0.875rem', flexShrink: 0 }}>
                    <RemarkField
                        task={task}
                        isFormDisabled={isFormDisabled}
                        onRemarkChange={handleRemarkChange}
                    />
                </Box>
                <Box sx={{ width: '50px', p: '4px 2px', display: 'flex', justifyContent: 'center', flexShrink: 0, backgroundColor: '#f1f8e9' }}>
                    <MemoizedCheckbox
                        checked={task.planDay}
                        disabled={true}
                        sx={{ '&.Mui-checked': { color: '#4CAF50' } }}
                    />
                </Box>
                <Box sx={{ width: '50px', p: '4px 2px', display: 'flex', justifyContent: 'center', flexShrink: 0, backgroundColor: '#e1f5fe' }}>
                    <MemoizedCheckbox
                        checked={task.actualDay}
                        onChange={(e) => handleActualDayChange(task, e.target.checked)}
                        disabled={isFormDisabled}
                    />
                </Box>
                <Box sx={{ width: '150px', p: '4px 8px', fontSize: '0.875rem', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={task.todo} arrow>
                        {task.todo}
                    </Tooltip>
                </Box>
            </Box>
        </div>
    );
}, (prevProps, nextProps) => {

    const prevTask = prevProps.data.filteredTasks[prevProps.index];
    const nextTask = nextProps.data.filteredTasks[nextProps.index];

    if (!prevTask || !nextTask) return false;

    return prevTask.actualDay === nextTask.actualDay &&
        prevTask.planDay === nextTask.planDay &&
        prevTask.remark === nextTask.remark &&
        prevTask.yojanaName === nextTask.yojanaName &&
        prevTask.areaName === nextTask.areaName &&
        prevTask.taskName === nextTask.taskName &&
        prevProps.data.isFormDisabled === nextProps.data.isFormDisabled;
});

const WeeklwiseAllocation = () => {
    const [isPreviousDisabled, setIsPreviousDisabled] = useState(false);
    const [shiftData, setShiftData] = useState([]);
    const [isNextDisabled, setIsNextDisabled] = useState(false);
    const [dropdownData, setDropdownData] = useState([]);
    const [yojna, setYojna] = useState([]);
    const [formData, setFormData] = useState({
        YojanaName: '',
        Abrv: '',
        Remark: '',
        Property: '',
        Shift: '',
        Yojna: '',
        WorkStation: '',
        WorkRecvDate: new Date().toISOString().split('T')[0]
    });

    const navigate = useNavigate();
    const location = useLocation();
    const [isFormDisabled, setIsFormDisabled] = useState(true);
    const [currentYojana, setCurrentYojana] = useState(null);
    const [mode, setMode] = useState('view');
    const [status, setStatus] = useState("1");
    const [lastInsertedYojanaId, setLastInsertedYojanaId] = useState(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [userId] = useState(sessionStorage.getItem('UserId'));
    const [dataForPrint, setDataForPrint] = useState({});
    const [rows, setRows] = useState([]);
    const [selectedWorkAreas, setSelectedWorkAreas] = useState([]);
    const [isRetrieveLoading, setIsRetrieveLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const contentRef = useRef(null);
    const handlePrint = useReactToPrint({ contentRef });
    const yojanaNameRef = useRef(null);
    const globalProperty = useSelector(selectGlobalProperty);
    const [processedTasks, setProcessedTasks] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const [selectedYojnas, setSelectedYojnas] = useState([]);
    const [actualFilter, setActualFilter] = useState(null);
    const isUpdatingRef = useRef(false);
    const dayMappingCache = useRef({});
    const taskUpdateTimeoutRef = useRef(null);
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);


    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const getTaskId = useCallback((task) => {
        return `${task.yojanaName}_${task.areaName}_${task.taskName}`;
    }, []);

    const getDayFromDate = useCallback((dateString) => {
        if (dayMappingCache.current[dateString]) {
            return dayMappingCache.current[dateString];
        }
        const date = new Date(dateString);
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const day = days[date.getDay()];
        dayMappingCache.current[dateString] = day;
        return day;
    }, []);

    const getFullDayName = useCallback((dayShort) => {
        const dayMap = {
            'Sun': 'Sunday',
            'Mon': 'Monday',
            'Tue': 'Tuesday',
            'Wed': 'Wednesday',
            'Thu': 'Thursday',
            'Fri': 'Friday',
            'Sat': 'Saturday'
        };
        return dayMap[dayShort] || dayShort;
    }, []);

    const handleYojnaMultiSelect = useCallback((event) => {
        const { value } = event.target;
        setSelectedYojnas(typeof value === 'string' ? value.split(',') : value);
        setFormData(prev => ({ ...prev, Yojna: value.join(',') }));
    }, []);

    const handleActualFilterChange = useCallback((event) => {
        const value = event.target.value;
        if (value === 'all') {
            setActualFilter(null);
        } else {
            setActualFilter(value === 'checked');
        }
    }, []);

    const currentDay = useMemo(() => getDayFromDate(formData.WorkRecvDate),
        [formData.WorkRecvDate, getDayFromDate]);
    const currentDayFullName = useMemo(() => getFullDayName(currentDay),
        [currentDay, getFullDayName]);

    const processWeeklyData = useCallback((apiData) => {
        if (!apiData || apiData.length === 0) return [];

        const groupedData = {};
        const currentDayStr = currentDay;

        apiData.forEach(item => {
            const yojanaName = item.YojanaName;
            const areaId = item.HkWrkAreaId;
            const areaName = item.HkWrkAreaName;
            const taskId = item.HkTaskId;
            const taskName = item.HkTaskName;
            const workStation = item.HkWrkStnName;
            const todo = item.msg;
            const hasPlanForToday = item[currentDayStr] === "1";

            if (hasPlanForToday) {
                const key = `${yojanaName}_${areaId}_${taskId}`;

                if (!groupedData[key]) {
                    groupedData[key] = {
                        yojanaName: yojanaName,
                        yojanaId: item.YojanaId,
                        areaName: areaName,
                        areaId: areaId,
                        taskName: taskName,
                        workStation: workStation,
                        taskId: taskId,
                        todo: todo,
                        wrkAllocId: item.WrkAllocId || 0,
                        wrkAllocTaskId: item.WrkAllocTaskId || 0,
                        originalData: item,
                        planDay: true,
                        actualDay: item[`${currentDayStr}A`] === "1",
                        remark: item.Remark || ''
                    };
                }
            }
        });

        return Object.values(groupedData);
    }, [currentDay]);

    useEffect(() => {
        if (rows.length > 0 && !isUpdatingRef.current) {
            const processedData = processWeeklyData(rows);
            setProcessedTasks(processedData);
        } else {
            setProcessedTasks([]);
        }
    }, [rows, processWeeklyData]);

    useEffect(() => {
        let filtered = processedTasks;

        if (debouncedSearchTerm.trim() !== '') {
            const searchTermLower = debouncedSearchTerm.toLowerCase().trim();
            filtered = filtered.filter(task =>
                task.yojanaName.toLowerCase().includes(searchTermLower) ||
                task.taskName.toLowerCase().includes(searchTermLower) ||
                task.areaName.toLowerCase().includes(searchTermLower) ||
                task.workStation.toLowerCase().includes(searchTermLower) ||
                task.todo.toLowerCase().includes(searchTermLower) ||
                (task.remark && task.remark.toLowerCase().includes(searchTermLower))
            );
        }

        if (actualFilter !== null) {
            filtered = filtered.filter(task => task.actualDay === actualFilter);
        }

        setFilteredTasks(filtered);
    }, [debouncedSearchTerm, processedTasks, actualFilter]);

    const taskIndexMap = useMemo(() => {
        const map = new Map();
        processedTasks.forEach((task, index) => {
            const key = `${task.yojanaName}_${task.areaName}_${task.taskName}`;
            map.set(key, index);
        });
        return map;
    }, [processedTasks]);

    const findTaskIndex = useCallback((task) => {
        const key = `${task.yojanaName}_${task.areaName}_${task.taskName}`;
        return taskIndexMap.get(key) ?? -1;
    }, [taskIndexMap]);

    const handleActualDayChange = useCallback((task, checked) => {
        if (isUpdatingRef.current || isFormDisabled) return;

        const taskIndex = findTaskIndex(task);
        if (taskIndex === -1) return;

        isUpdatingRef.current = true;

        setProcessedTasks(prev => {
            const newTasks = [...prev];
            newTasks[taskIndex] = {
                ...newTasks[taskIndex],
                actualDay: checked
            };
            return newTasks;
        });


        setTimeout(() => {
            isUpdatingRef.current = false;
        }, 50);
    }, [findTaskIndex, isFormDisabled, mode]);

    const handleRemarkChange = useCallback((task, remarkValue) => {
        if (isFormDisabled) return;

        const taskIndex = findTaskIndex(task);
        if (taskIndex === -1) return;

        if (taskUpdateTimeoutRef.current) {
            clearTimeout(taskUpdateTimeoutRef.current);
        }

        taskUpdateTimeoutRef.current = setTimeout(() => {
            setProcessedTasks(prev => {
                const newTasks = [...prev];
                if (newTasks[taskIndex] && newTasks[taskIndex].remark !== remarkValue) {
                    newTasks[taskIndex] = {
                        ...newTasks[taskIndex],
                        remark: remarkValue
                    };
                    return newTasks;
                }
                return prev;
            });
        }, 100);
    }, [findTaskIndex, isFormDisabled]);

    const handleAllTasksChange = useCallback((checked) => {
        if (isUpdatingRef.current || isFormDisabled) return;

        isUpdatingRef.current = true;

        const filteredTaskKeys = new Set(filteredTasks.map(getTaskId));

        setProcessedTasks(prev =>
            prev.map(task => {
                const taskKey = getTaskId(task);
                if (filteredTaskKeys.has(taskKey)) {
                    return { ...task, actualDay: checked };
                }
                return task;
            })
        );

        setTimeout(() => {
            isUpdatingRef.current = false;
        }, 50);
    }, [filteredTasks, getTaskId, isFormDisabled]);

    const handleSearch = useCallback((event) => {
        setSearchTerm(event.target.value);
    }, []);

    const clearSearch = useCallback(() => {
        setSearchTerm('');
    }, []);

    const allTasksChecked = useMemo(() => {
        if (filteredTasks.length === 0) return false;
        return filteredTasks.every(task => task.actualDay);
    }, [filteredTasks]);

    const someTasksChecked = useMemo(() => {
        if (filteredTasks.length === 0) return false;
        const checkedCount = filteredTasks.filter(task => task.actualDay).length;
        return checkedCount > 0 && checkedCount < filteredTasks.length;
    }, [filteredTasks]);

    const taskIdMap = useMemo(() => {
        const map = new Map();
        filteredTasks.forEach(task => {
            const key = `${task.yojanaName}_${task.areaName}_${task.taskName}`;
            map.set(key, true);
        });
        return map;
    }, [filteredTasks]);

    useEffect(() => {
        const fetchYojnaData = async () => {
            try {
                const response = await getYojnaMasterdropdata({
                    Flag: "",
                    YojanaId: 1
                });
                if (response?.data?.Status === 0) {
                    const dropdownItems = response.data.Data.map((item) => ({
                        id: item.YojanaId,
                        name: item.YojanaName,
                    }));
                    setYojna(dropdownItems);
                    if (dropdownItems.length > 0) {
                        setFormData(prev => ({ ...prev, Yojna: dropdownItems[0].id }));
                    }
                }
            } catch (error) {
                toast.error("Error fetching yojna data");
            }
        };
        fetchYojnaData();
    }, []);

    useEffect(() => {
        if (shiftData.length > 0 && location.state?.ShiftId) {
            const correctShift = shiftData.find(item => item.id === location.state.ShiftId);
            if (correctShift) {
                setFormData(prev => ({ ...prev, Shift: correctShift.id.toString() }));
            }
        }
    }, [shiftData, location.state?.ShiftId]);

    useEffect(() => {
        if (yojna.length > 0 && location.state?.YojanaId) {
            const correctYojna = yojna.find(item => item.id === location.state.YojanaId);
            if (correctYojna) {
                setFormData(prev => ({ ...prev, Yojna: correctYojna.id.toString() }));
            }
        }
    }, [yojna, location.state?.YojanaId]);

    useEffect(() => {
        if (dropdownData.length > 0 && location.state?.PropId) {
            const correctProp = dropdownData.find(item => item.id === location.state.PropId);
            if (correctProp) {
                setFormData(prev => ({ ...prev, Property: correctProp.id.toString() }));
            }
        }
    }, [dropdownData, location.state?.PropId]);

    const handlePrevious = useCallback(async () => {
        if (currentYojana && currentYojana > 1 && !isPreviousDisabled) {
            await handleRetrieve();
        }
    }, [currentYojana, isPreviousDisabled]);

    const handleNext = useCallback(async () => {
        if (currentYojana) {
            await handleRetrieve();
        }
    }, [currentYojana]);

    const handleInputChange = useCallback((event) => {
        const { name, value } = event.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    }, []);

    const handleRetrieve = useCallback(async () => {
        if (!formData.Property) {
            toast.error('Property is required');
            return;
        }
        if (!formData.Shift) {
            toast.error('Shift is required');
            return;
        }

        setOpenBackdrop(true);
        setIsRetrieveLoading(true);

        setRows([]);
        setSelectedWorkAreas([]);
        setProcessedTasks([]);
        setFilteredTasks([]);
        clearSearch();
        setActualFilter(null);

        try {
            const payload = {
                PropId: parseInt(formData.Property),
                YojanaId: 1,
                ShiftId: parseInt(formData.Shift),
                HkWrkAreaId: 1,
                WrkRecvDt: formData.WorkRecvDate,
                StrHkTaskId: '',
                StrYojanaId: selectedYojnas.length > 0 ? selectedYojnas.join(',') : '',
                StrHkWrkAreaId: ''
            };

            const response = await axiosInstance.post(
                '/MstYojana/RetriveTrnWrkRecvTaskWeekAll',
                payload
            );

            if (response.data?.Status === 0) {
                if (response.data.Data?.TrnWrkRecvTaskList && Array.isArray(response.data.Data.TrnWrkRecvTaskList)) {
                    const taskList = response.data.Data.TrnWrkRecvTaskList;

                    const processedTaskList = taskList.map(task => {
                        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                        const updatedTask = { ...task };

                        days.forEach(day => {
                            if (!updatedTask[day]) updatedTask[day] = "0";
                            if (!updatedTask[`${day}A`]) updatedTask[`${day}A`] = "0";
                        });

                        return updatedTask;
                    });

                    setRows(processedTaskList);
                    setIsFormDisabled(mode === 'view');

                } else {
                    setRows([]);
                    toast.info(response.data?.Message || "No data found for this combination");
                }
            } else {
                setRows([]);
                toast.info(response.data?.Message || "No data available");
            }
        } catch (error) {
            toast.error("Error loading data");
            setRows([]);
        } finally {
            setIsRetrieveLoading(false);
            setOpenBackdrop(false);
        }
    }, [formData.Property, formData.Shift, formData.WorkRecvDate, selectedYojnas]);


    // useEffect(() => {
    //     if (location.state?.propId && location.state?.shiftId && location.state?.date) {
    //         const propId = location.state.propId.toString();
    //         const shiftId = location.state.shiftId.toString();
    //         let date = location.state.date;


    //         if (date && date.includes('T')) {
    //             date = date.split('T')[0];
    //         }

    //         console.log("Loading data for propId:", propId, "shiftId:", shiftId, "date:", date); 

    //         setRows([]);
    //         setProcessedTasks([]);
    //         setFilteredTasks([]);
    //         clearSearch();
    //         setActualFilter(null);
    //         setMode('view');
    //         setIsFormDisabled(true);


    //         setFormData(prev => ({
    //             ...prev,
    //             Property: propId,
    //             Shift: shiftId,
    //             WorkRecvDate: date, 
    //         }));

    //         setTimeout(() => {
    //             const callRetrieve = async () => {
    //                 setOpenBackdrop(true);
    //                 setIsRetrieveLoading(true);

    //                 try {
    //                     const payload = {
    //                         PropId: parseInt(propId),
    //                         YojanaId: 1,
    //                         ShiftId: parseInt(shiftId),
    //                         HkWrkAreaId: 1,
    //                         WrkRecvDt: date, 
    //                         StrHkTaskId: '',
    //                         StrYojanaId: selectedYojnas.length > 0 ? selectedYojnas.join(',') : '',
    //                         StrHkWrkAreaId: ''
    //                     };

    //                     const response = await axiosInstance.post(
    //                         '/MstYojana/RetriveTrnWrkRecvTaskWeekAll',
    //                         payload
    //                     );

    //                     if (response.data?.Status === 0) {
    //                         if (response.data.Data?.TrnWrkRecvTaskList && Array.isArray(response.data.Data.TrnWrkRecvTaskList)) {
    //                             const taskList = response.data.Data.TrnWrkRecvTaskList;
    //                             const processedTaskList = taskList.map(task => {
    //                                 const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    //                                 const updatedTask = { ...task };
    //                                 days.forEach(day => {
    //                                     if (!updatedTask[day]) updatedTask[day] = "0";
    //                                     if (!updatedTask[`${day}A`]) updatedTask[`${day}A`] = "0";
    //                                 });
    //                                 return updatedTask;
    //                             });
    //                             setRows(processedTaskList);
    //                             setIsFormDisabled(true);
    //                         } else {
    //                             setRows([]);
    //                             toast.info(response.data?.Message || "No data found for this combination");
    //                         }
    //                     } else {
    //                         setRows([]);
    //                         toast.info(response.data?.Message || "No data available");
    //                     }
    //                 } catch (error) {
    //                     toast.error("Error loading data");
    //                     setRows([]);
    //                 } finally {
    //                     setIsRetrieveLoading(false);
    //                     setOpenBackdrop(false);
    //                 }
    //             };

    //             callRetrieve();
    //         }, 100);
    //     } 
    //     else if (!location.state) {
    //         setMode('add');
    //         setIsFormDisabled(false);
    //         setSelectedWorkAreas([]);
    //         setRows([]);
    //         setProcessedTasks([]);
    //         setFilteredTasks([]);
    //         clearSearch();
    //         setActualFilter(null);
    //         setFormData(prev => ({
    //             ...prev,
    //             WorkRecvDate: new Date().toISOString().split('T')[0]
    //         }));
    //     }
    // }, [location.state]);



    const fetchDataWithParams = useCallback(async (propId, shiftId, date, yojnaIds = []) => {
        if (!propId || !shiftId) {
            toast.error('Property and Shift are required');
            return;
        }

        let formattedDate = date;
        if (formattedDate && formattedDate.includes('T')) {
            formattedDate = formattedDate.split('T')[0];
        }

        setOpenBackdrop(true);
        setIsRetrieveLoading(true);
        setRows([]);
        setSelectedWorkAreas([]);
        setProcessedTasks([]);
        setFilteredTasks([]);
        clearSearch();
        setActualFilter(null);

        try {
            const payload = {
                PropId: parseInt(propId),
                YojanaId: 1,
                ShiftId: parseInt(shiftId),
                HkWrkAreaId: 1,
                WrkRecvDt: formattedDate,
                StrHkTaskId: '',
                StrYojanaId: '',
                StrHkWrkAreaId: ''
            };

            const response = await axiosInstance.post(
                '/MstYojana/RetriveTrnWrkRecvTaskWeekAll',
                payload
            );

            if (response.data?.Status === 0) {
                if (response.data.Data?.TrnWrkRecvTaskList && Array.isArray(response.data.Data.TrnWrkRecvTaskList)) {
                    const taskList = response.data.Data.TrnWrkRecvTaskList;
                    const processedTaskList = taskList.map(task => {
                        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                        const updatedTask = { ...task };
                        days.forEach(day => {
                            if (!updatedTask[day]) updatedTask[day] = "0";
                            if (!updatedTask[`${day}A`]) updatedTask[`${day}A`] = "0";
                        });
                        return updatedTask;
                    });
                    setRows(processedTaskList);
                    setIsFormDisabled(true);
                } else {
                    setRows([]);
                    toast.info(response.data?.Message || "No data found for this combination");
                }
            } else {
                setRows([]);
                toast.info(response.data?.Message || "No data available");
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error("Error loading data");
            setRows([]);
        } finally {
            setIsRetrieveLoading(false);
            setOpenBackdrop(false);
        }
    }, []);

    useEffect(() => {
        const loadDataFromNavigation = async () => {
            if (location.state?.propId && location.state?.shiftId && location.state?.date) {
                const propId = location.state.propId.toString();
                const shiftId = location.state.shiftId.toString();
                let date = location.state.date;

                if (date && date.includes('T')) {
                    date = date.split('T')[0];
                }

                setRows([]);
                setProcessedTasks([]);
                setFilteredTasks([]);
                clearSearch();
                setActualFilter(null);
                setMode('view');
                setIsFormDisabled(true);

                if (dropdownData.length > 0 && shiftData.length > 0) {

                    setFormData(prev => ({
                        ...prev,
                        Property: propId,
                        Shift: shiftId,
                        WorkRecvDate: date,
                    }));


                    await fetchDataWithParams(propId, shiftId, date, selectedYojnas);
                    setIsInitialLoadComplete(true);
                }
            }
            else if (!location.state) {
                setMode('add');
                setIsFormDisabled(false);
                setSelectedWorkAreas([]);
                setRows([]);
                setProcessedTasks([]);
                setFilteredTasks([]);
                clearSearch();
                setActualFilter(null);
                setFormData(prev => ({
                    ...prev,
                    WorkRecvDate: new Date().toISOString().split('T')[0]
                }));
                setIsInitialLoadComplete(true);
            }
        };


        if (dropdownData.length > 0 && shiftData.length > 0) {
            loadDataFromNavigation();
        }
    }, [location.state, dropdownData, shiftData]);



    const handleSubmit = useCallback(async () => {
        const submissionData = [];
        const groupedTasks = {};

        if (rows.length === 0 || processedTasks.length === 0) {
            toast.error('No data to submit');
            return;
        }

        const firstRow = rows[0];
        const allDayFields = [];
        Object.keys(firstRow).forEach(key => {
            if (['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].includes(key)) {
                allDayFields.push(key);
            }
        });

        processedTasks.forEach(task => {
            const originalData = rows.find(row =>
                row.YojanaName === task.yojanaName &&
                row.HkWrkAreaName === task.areaName &&
                row.HkTaskName === task.taskName
            );

            if (originalData) {
                const key = `${originalData.YojanaId}_${originalData.HkWrkAreaId}`;

                if (!groupedTasks[key]) {
                    groupedTasks[key] = {
                        WrkRecvId: originalData.WrkRecvId || 0,
                        WrkRecvNo: originalData.WrkRecvId ? `TW${String(originalData.WrkRecvId).padStart(6, '0')}` : '',
                        WrkRecvDt: `${formData.WorkRecvDate}T10:30:00`,
                        HkWrkrId: 1,
                        WrkAllocId: originalData.WrkAllocId || 0,
                        PropId: parseInt(formData.Property),
                        HkWrkAreaId: originalData.HkWrkAreaId,
                        YojanaId: originalData.YojanaId,
                        ShiftId: parseInt(formData.Shift),
                        Remark: `Work received for ${currentDayFullName} shift`,
                        CreatedBy: parseInt(userId) || 1,
                        UpdatedBy: parseInt(userId) || 1,
                        MstYojanaEntities: []
                    };
                }

                const dayObject = {};

                allDayFields.forEach(day => {
                    dayObject[day] = originalData[day] || "0";

                    if (day === currentDay) {
                        dayObject[`${day}A`] = task.actualDay ? "1" : "0";
                    } else {
                        dayObject[`${day}A`] = "0";
                    }
                });

                if (!allDayFields.includes(currentDay)) {
                    dayObject[currentDay] = originalData[currentDay] || "0";
                    dayObject[`${currentDay}A`] = task.actualDay ? "1" : "0";
                }

                dayObject.Remark = task.remark || '';

                groupedTasks[key].MstYojanaEntities.push({
                    WrkRecvTaskId: 0,
                    WrkRecvId: 0,
                    WrkAllocId: originalData.WrkAllocId || 0,
                    WrkAllocTaskId: originalData.WrkAllocTaskId || 0,
                    HkTaskId: originalData.HkTaskId,
                    ...dayObject,

                    Remark: task.remark || ''
                });
            }
        });

        Object.keys(groupedTasks).forEach(key => {
            submissionData.push(groupedTasks[key]);
        });

        setIsSubmitting(true);
        setOpenBackdrop(true);

        try {
            const response = await axiosInstance.post(
                '/MstYojana/MangeTrnWrkRecv',
                submissionData
            );

            if (response.data.Status === 0) {
                toast.success(response.data.Message || 'Data submitted successfully');
                setIsFormDisabled(true);
                setMode('view');
                // await handleRetrieve();
            } else {
                toast.error(response.data.Message || 'Submission failed');
            }
        } catch (error) {
            console.error('Submission error:', error);
            toast.error('Error submitting data');
        } finally {
            setIsSubmitting(false);
            setOpenBackdrop(false);
        }
    }, [rows, processedTasks, formData, userId, currentDay, currentDayFullName, handleRetrieve]);

    const handleEdit = useCallback(async () => {
        let flag = await checkAndLock(currentYojana, "MstYojana", "YojanaId");
        switch (flag) {
            case 0:
                setMode("edit");
                setIsFormDisabled(false);
                break;
            case 1:
                toast.info("Record Already Locked.", { autoClose: 500 });
                break;
            case 2:
                toast.error("Record Failed To Lock.", { autoClose: 500 });
                break;
            default:
                toast.error("Something Went Wrong.", { autoClose: 500 });
        }
    }, [currentYojana]);

    const handleAdd = useCallback(() => {
        setMode('add');
        setIsFormDisabled(false);
        setCurrentYojana(null);
        setStatus("1");
        setSelectedWorkAreas([]);
        setRows([]);
        setProcessedTasks([]);
        setFilteredTasks([]);
        clearSearch();
        setActualFilter(null);
        setFormData(prev => ({
            ...prev,
            YojanaName: '',
            Abrv: '',
            Remark: '',
        }));

        setTimeout(() => {
            yojanaNameRef.current?.focus();
        }, 0);
    }, []);

    const handleCancel = useCallback(async () => {
        try {
            if (mode === 'edit' && currentYojana) {
                await handleRetrieve();
                await UnLock(currentYojana, "MstYojana", "YojanaId");
            } else if (mode === 'add') {
                if (lastInsertedYojanaId) {
                    await handleRetrieve();
                    setCurrentYojana(lastInsertedYojanaId);
                } else {
                    setMode('view');
                    setIsFormDisabled(true);
                    // setRows([]);
                    // setProcessedTasks([]);
                    // setFilteredTasks([]);
                    clearSearch();
                }
            } else {
                setMode('view');
                setIsFormDisabled(true);
            }
        } catch (error) {
            toast.error('Error occurred while cancelling. Please try again.');
        }
    }, [mode, currentYojana, lastInsertedYojanaId, handleRetrieve])

    useEffect(() => {
        if (globalProperty && globalProperty !== formData.Property && !location.state) {
            setFormData(prevState => ({
                ...prevState,
                Property: globalProperty.toString()
            }));
        }
    }, [globalProperty, formData.Property, location.state]);

    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const response = await getRoomMasterdropdata({
                    Flag: "",
                    UserId: userId,
                });
                if (response?.data?.Status === 0) {
                    const dropdownItems = response.data.Data?.map((item) => ({
                        id: item.PropId,
                        name: item.Name,
                    }));
                    setDropdownData(dropdownItems);
                    if (!location.state?.propId) {
                        if (globalProperty) {
                            const globalPropExists = dropdownItems.find(item =>
                                item.id.toString() === globalProperty.toString()
                            );
                            if (globalPropExists) {
                                setFormData(prev => ({
                                    ...prev,
                                    Property: globalProperty.toString()
                                }));
                            } else if (dropdownItems.length > 0) {
                                setFormData(prev => ({
                                    ...prev,
                                    Property: dropdownItems[0].id.toString()
                                }));
                            }
                        } else if (dropdownItems.length > 0) {
                            setFormData(prev => ({
                                ...prev,
                                Property: dropdownItems[0].id.toString()
                            }));
                        }
                    }
                }
            } catch (error) {
                toast.error("Error fetching property data");
            }
        };
        fetchDropdownData();
    }, [userId, globalProperty, location.state]);

    useEffect(() => {
        const fetchShiftData = async () => {
            try {
                const response = await getShiftMasterdropdata();
                if (response?.data?.Status === 0) {
                    const dropdownItems = response.data.Data.map((item) => ({
                        id: item.ShiftId,
                        name: item.ShiftName,
                    }));
                    setShiftData(dropdownItems);
                    if (!location.state?.shiftId && dropdownItems.length > 0) {
                        setFormData(prev => ({ ...prev, Shift: dropdownItems[0].id.toString() }));
                    } else if (location.state?.shiftId) {

                        const shiftExists = dropdownItems.find(item =>
                            item.id.toString() === location.state.shiftId.toString()
                        );
                        if (shiftExists) {
                            setFormData(prev => ({ ...prev, Shift: location.state.shiftId.toString() }));
                        }
                    }
                }
            } catch (error) {
                toast.error("Error fetching shift data");
            }
        };
        fetchShiftData();
    }, [location.state]);

    const handleExit = useCallback(() => {
        navigate('/operations/weeklyallocationtable');
    }, [navigate]);

    const handleDeleteClick = useCallback(() => {
        setOpenConfirmDialog(true);
    }, []);

    const itemData = useMemo(() => ({
        filteredTasks,
        isFormDisabled,
        handleActualDayChange,
        handleRemarkChange,
        currentDay,
        taskIdMap
    }), [filteredTasks, isFormDisabled, handleActualDayChange, handleRemarkChange, currentDay, taskIdMap]);

    const rowHeight = 30;

    return (
        <>
            <Box className="form-container" sx={{
                width: { xs: "calc(100vw )", sm: "auto", md: "auto" },
                maxWidth: { xs: "calc(100vw )", sm: "auto", md: "auto" },
            }}>
                <ToastContainer />
                <Backdrop
                    sx={{
                        color: '#fff',
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)'
                    }}
                    open={openBackdrop}
                >
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                    }}>
                        <CircularProgress color="inherit" />
                        <Typography variant="h6" sx={{ color: 'white' }}>
                            {isSubmitting ? 'Submitting Data...' : 'Loading Data...'}
                        </Typography>
                    </Box>
                </Backdrop>

                <Grid container spacing={1} className="form_grid" sx={{ width: '90% !important' }}>
                    <Grid item xs={12} className="form_title"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginTop: '20px',
                            flexDirection: { xs: 'column', sm: 'row', md: 'row' },
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                margin: 'auto',
                                textAlign: { xs: 'center', sm: 'center' },
                                fontSize: { xs: '24px', sm: '18px', md: '24px' },
                                fontWeight: 'bold',
                                color: '#333',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <IconButton
                                onClick={() => navigate('/operations/housekeepin')}
                                sx={{
                                    padding: '4px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        transform: 'scale(1.05)'
                                    },
                                    transition: 'transform 0.2s'
                                }}
                            >
                                <ArrowBackIcon sx={{ color: 'black' }} />
                            </IconButton> Daily Task Entry - {currentDayFullName}
                        </Typography>

                        <Grid sx={{ display: 'flex', justifyContent: 'end' }}>
                            <CrudButtons
                                moduleName="Task/Work Entry"
                                mode={mode}
                                onAdd={handleAdd}
                                onEdit={handleEdit}
                                onView={handlePrint}
                                onDelete={handleDeleteClick}
                                onExit={handleExit}
                                onPrevious={handlePrevious}
                                onNext={handleNext}
                                readOnlyMode={isFormDisabled}
                                hideDelete={true}
                            />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} className="form_field" mt={1}>
                        <Box sx={{ width: '100%' }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={2.5}>
                                    <FormControl variant="filled" fullWidth className="custom-textfield" size="small">
                                        <InputLabel id="property-label">Property</InputLabel>
                                        <Select
                                            labelId="property-label"
                                            id="Property"
                                            name="Property"
                                            value={formData.Property}
                                            onChange={handleInputChange}
                                            disabled={isFormDisabled}
                                        >
                                            {dropdownData.map((item) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={2.5}>
                                    <FormControl variant="filled" fullWidth className="custom-textfield" size="small">
                                        <InputLabel id="shift-label">Shift</InputLabel>
                                        <Select
                                            labelId="shift-label"
                                            id="Shift"
                                            name="Shift"
                                            value={formData.Shift}
                                            onChange={handleInputChange}
                                            disabled={isFormDisabled}
                                        >
                                            {shiftData.map((item) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={2.5}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        size="small"
                                        type="date"
                                        label="Work Received Date"
                                        name="WorkRecvDate"
                                        value={formData.WorkRecvDate}
                                        onChange={handleInputChange}
                                        disabled={isFormDisabled}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        className="custom-textfield"
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <FormControl variant="filled" fullWidth className="custom-textfield">
                                        <InputLabel id="yojna-multi-label">Yojna Name</InputLabel>
                                        <Select
                                            labelId="yojna-multi-label"
                                            id="Yojna"
                                            name="Yojna"
                                            multiple
                                            value={selectedYojnas}
                                            onChange={handleYojnaMultiSelect}
                                            disabled={isFormDisabled}
                                            renderValue={(selected) => {
                                                if (selected.length === 0) {
                                                    return <em>Select Yojna(s)</em>;
                                                }
                                                return selected.map(id => {
                                                    const item = yojna.find(item => item.id.toString() === id.toString());
                                                    return item?.name || id;
                                                }).join(', ');
                                            }}
                                            MenuProps={MenuProps}
                                        >
                                            {yojna.map((item) => (
                                                <MenuItem key={item.id} value={item.id.toString()}>
                                                    <Checkbox checked={selectedYojnas.indexOf(item.id.toString()) > -1} sx={{ padding: "2px" }} />
                                                    <ListItemText primary={item.name} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={1}>
                                    <Button
                                        size='small'
                                        variant="contained"
                                        onClick={handleRetrieve}
                                        disabled={isRetrieveLoading || isFormDisabled}
                                        sx={{
                                            bgcolor: '#635bff',
                                            '&:hover': { bgcolor: '#5248cc' },
                                            padding: '8px 16px',
                                            fontSize: '0.75rem',
                                            minWidth: '80px'
                                        }}
                                    >
                                        {isRetrieveLoading ? 'Loading...' : 'Retrieve'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    {/* {processedTasks.length > 0 && ( */}
                    <Grid item xs={12} mt={1}>
                        <Paper sx={{
                            width: '100%',
                            overflow: 'hidden',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            borderRadius: '8px',
                            minHeight: '400px'
                        }}>
                            <Box sx={{
                                p: 0.5,
                                py: 0.8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                backgroundColor: '#f8f9fa',
                                borderBottom: '1px solid #e0e0e0',
                                flexWrap: 'wrap'
                            }}>
                                <TextField
                                    size="small"
                                    placeholder="Search by Yojana, Task, Work Area/Station or Remark..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ fontSize: '1rem', color: '#666' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchTerm && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={clearSearch}
                                                    edge="end"
                                                    sx={{ padding: '2px' }}
                                                >
                                                    <CloseIcon sx={{ fontSize: '0.95rem' }} />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        width: '530px',
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'white',
                                            '&:hover': { backgroundColor: 'white' },
                                            '& input': { padding: '6px 14px', fontSize: '0.8rem' }
                                        }
                                    }}
                                />

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                        Actual:
                                    </Typography>
                                    <FormControl size="small" sx={{ minWidth: 100 }}>
                                        <Select
                                            value={actualFilter === null ? 'all' : actualFilter ? 'checked' : 'unchecked'}
                                            onChange={handleActualFilterChange}
                                            displayEmpty
                                            sx={{
                                                fontSize: '0.7rem',
                                                height: '28px',
                                                backgroundColor: 'white',
                                                '& .MuiSelect-select': { padding: '4px 8px' }
                                            }}
                                        >
                                            <MenuItem value="all">All</MenuItem>
                                            <MenuItem value="checked">Checked </MenuItem>
                                            <MenuItem value="unchecked">Unchecked </MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem', ml: 'auto' }}>
                                    Showing {filteredTasks.length} of {processedTasks.length} tasks
                                </Typography>
                            </Box>

                            <Box sx={{
                                borderBottom: '1px solid #dee2e6',
                                backgroundColor: '#f0f0f0'
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    backgroundColor: '#e9ecef',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    borderBottom: '1px solid #ced4da',
                                    '& > div': {
                                        padding: '6px 8px',
                                        borderRight: '1px solid #dee2e6',
                                        '&:last-child': { borderRight: 'none' }
                                    }
                                }}>
                                    <Box sx={{ width: '60px', flexShrink: 0 }}>Sr No.</Box>
                                    <Box sx={{ width: '250px', flexShrink: 0 }}>Yojana</Box>
                                    <Box sx={{ width: '150px', flexShrink: 0 }}>Work Station</Box>
                                    <Box sx={{ width: '150px', flexShrink: 0 }}>Work Area</Box>
                                    <Box sx={{ width: '150px', flexShrink: 0 }}>Task</Box>
                                    <Box sx={{ width: '250px', flexShrink: 0 }}>Remark</Box>
                                    <Box sx={{ width: '50px', flexShrink: 0, textAlign: 'center', backgroundColor: '#f1f8e9' }}>
                                        P
                                    </Box>
                                    <Box sx={{ width: '50px', flexShrink: 0, textAlign: 'center', backgroundColor: '#e1f5fe' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            A
                                            <MemoizedCheckbox
                                                checked={allTasksChecked}
                                                indeterminate={someTasksChecked}
                                                onChange={(e) => handleAllTasksChange(e.target.checked)}
                                                disabled={isFormDisabled || filteredTasks.length === 0}
                                                size="small"
                                            />
                                        </Box>
                                    </Box>
                                    <Box sx={{ width: '250px', flexShrink: 0 }}>Todo List</Box>
                                </Box>
                            </Box>

                            {filteredTasks.length > 0 ? (
                                <Box sx={{
                                    height: Math.min(400, filteredTasks.length * rowHeight + 2),
                                    width: '100%',
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                        height: '5px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#888',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: '#555',
                                    },
                                }}>
                                    <AutoSizer>
                                        {({ height, width }) => (
                                            <List
                                                height={height}
                                                itemCount={filteredTasks.length}
                                                itemSize={rowHeight}
                                                width={width}
                                                itemData={itemData}
                                                overscanCount={5}
                                            >
                                                {TaskRow}
                                            </List>
                                        )}
                                    </AutoSizer>
                                </Box>
                            ) : (
                                <Box sx={{ py: 4, textAlign: 'center', backgroundColor: '#fff' }}>
                                    {/* <HourglassEmptyIcon
                                        sx={{
                                            fontSize: 60,
                                            color: '#a0aec0',
                                            animation: `${rotate360} 2s linear infinite`,
                                        }}
                                    /> */}
                                    <Typography variant="body1" color="textSecondary" sx={{ fontSize: '1rem' }}>
                                        {processedTasks.length === 0 ? (
                                            `No tasks scheduled for ${currentDayFullName} adjust filters and try again!`
                                        ) : (
                                            `No tasks found matching current filters${searchTerm ? ` - Search: "${searchTerm}"` : ''
                                            }${actualFilter !== null ? ` - Actual: ${actualFilter ? 'Checked' : 'Unchecked'}` : ''}`
                                        )}
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                    {/* )} */}

                    <Grid item xs={12} className="form_button" mt={1}>
                        {mode === 'view' && (
                            <>
                                <Button
                                    variant="contained"
                                    sx={{
                                        mr: 1,
                                        background: 'linear-gradient(290deg, #c9f0f3, #5d82ac)',
                                        padding: '6px 20px',
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        '&:hover': { background: 'linear-gradient(290deg, #b8e3e6, #4c6e94)' }
                                    }}
                                    disabled={processedTasks.length === 0 || isFormDisabled}
                                    onClick={handleSubmit}
                                >
                                    Submit
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={{
                                        mr: 1,
                                        background: 'linear-gradient(290deg, #a7c5e9, #8fabf3)',
                                        padding: '6px 20px',
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        '&:hover': { background: 'linear-gradient(290deg, #94b2d6, #7e9ae1)' }
                                    }}
                                    disabled={isFormDisabled}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                            </>
                        )}
                        {(mode === 'edit' || mode === 'add') && (
                            <>
                                <Button
                                    variant="contained"
                                    sx={{
                                        mr: 1,
                                        background: "linear-gradient(290deg, #b9d0e9, #8aafcf)",
                                        padding: '6px 20px',
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        '&:hover': { background: "linear-gradient(290deg, #a6bdd6, #7a9cb8)" }
                                    }}
                                    onClick={handleSubmit}
                                    disabled={processedTasks.length === 0 || isSubmitting || isFormDisabled}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit'}
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={{
                                        mr: 1,
                                        background: "linear-gradient(290deg, #b9d0e9, #608fb8)",
                                        padding: '6px 20px',
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        '&:hover': { background: "linear-gradient(290deg, #a6bdd6, #4f7a9e)" }
                                    }}
                                    disabled={isFormDisabled}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Box>

            <div style={{ display: 'none' }}>
                <DailyTaskPrint
                    ref={contentRef}
                    processedTasks={filteredTasks.length > 0 ? filteredTasks : processedTasks}
                    propertyName={
                        dropdownData.length > 0 && formData.Property
                            ? dropdownData.find(item =>
                                item.id.toString() === formData.Property.toString()
                            )?.name || 'N/A'
                            : 'N/A'
                    }
                    shiftName={
                        shiftData.length > 0 && formData.Shift
                            ? shiftData.find(item =>
                                item.id.toString() === formData.Shift.toString()
                            )?.name || 'N/A'
                            : 'N/A'
                    }
                    searchCriteria={searchTerm}
                    title="Weekly Task Allocation Report"
                    workRecvDate={formData.WorkRecvDate}
                />
            </div>
        </>
    );
};

export default WeeklwiseAllocation;